import { Modal } from "@mui/material";
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import closeIcon from "assets/images/components/app-header/close-icon.svg";
import api from "services/api";
import serveRequestErrors from "common/serve-request-errors";
import getNotificationRoute, { hasNotificationRoute } from "common/notification-routes";
import { getDaysDifference, getISOString, getMonths, getWeekdays, splitDateTime } from "common/calendar";
import classNames from "common/class-names";

const AppNotificationModal = (props, ref) => {
	const [visible, setVisible] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const [loading, setLoading] = useState(false);
	const cursorIdRef = useRef();
	const containerRef = useRef();
	const navigate = useNavigate();

	const onHandleGetList = useCallback(
		async (cursorId = null) => {
			if (loading) return;
			setLoading(true);

			let response = null;

			try {
				const params = { cursor: cursorId, portalType: "EMPLOYEE" };

				response = await api.get.notification.notificationList(params);
			} catch (error) {
				serveRequestErrors(error);
			}

			if (response) {
				if (response.length === 0) {
					setHasMore(false);
				}
				setNotifications((prev) => [...prev, ...response]);

				if (response.length > 0) {
					cursorIdRef.current = response[response.length - 1].id;
				}

				setLoading(false);
			}
		},
		[loading]
	);

	const onHandleMarkAsRead = useCallback(
		async (id) => {
			try {
				const isUnread = notifications.some((n) => n.id === id && !n.isRead);

				if (!isUnread) return;

				setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
				props.onUnreadChange?.((prev) => Math.max(prev - 1, 0));

				// awaited so the unread count refetch triggered by navigating does not race it
				await api.post.notification.markAsRead(id);
			} catch (error) {
				serveRequestErrors(error);
			}
		},
		[notifications, props]
	);

	const onHandleMarkAllAsRead = useCallback(() => {
		try {
			api.post.notification.markAllAsRead();
			setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
			props.onUnreadChange?.(0);
		} catch (error) {
			serveRequestErrors(error);
		}
	}, [props]);

	const groupedNotifications = useMemo(() => {
		const grouped = [];
		const now = new Date();

		notifications?.forEach((n) => {
			const createdDate = new Date(getISOString(n.createdDate));
			const daysDifference = getDaysDifference(createdDate, now);

			const splitedDateTime = splitDateTime(createdDate);
			let label = splitedDateTime.date;
			let timestamp = splitedDateTime.time;

			if (daysDifference === 0) {
				const diffMs = now.getTime() - createdDate.getTime();
				const diffMinutes = Math.floor(diffMs / 1000 / 60);
				const diffHours = Math.floor(diffMinutes / 60);

				label = "Today";
				if (diffMinutes < 60) {
					timestamp = `${diffMinutes} min ago`;
				} else {
					timestamp = `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
				}
			} else if (daysDifference === 1) {
				label = "Yesterday";
			} else {
				const daysOfWeek = getWeekdays("dddd");
				const monthNames = getMonths("MMM");

				const day = daysOfWeek[(createdDate.getDay() || 7) - 1].day;
				const month = monthNames[createdDate.getMonth()].month;

				label = `${day} ${createdDate.getDate()} ${month} ${createdDate.getFullYear()}`;
			}

			if (!grouped[label]) grouped[label] = [];
			grouped[label].push({ ...n, timestamp });
		});

		return grouped;
	}, [notifications]);

	const handleScroll = useCallback(() => {
		const container = containerRef.current;
		if (!container || loading || !hasMore) return;

		const { scrollTop, clientHeight, scrollHeight } = container;
		const threshold = 150;

		if (scrollTop + clientHeight >= scrollHeight - threshold) {
			onHandleGetList(cursorIdRef.current);
		}
	}, [loading, hasMore, onHandleGetList]);

	const onHandleShow = useCallback(() => {
		setVisible(true);
		setNotifications([]);
		setHasMore(true);
		cursorIdRef.current = null;
		onHandleGetList();
	}, [onHandleGetList]);

	const onHandleDismiss = useCallback(() => {
		setVisible(false);
	}, []);

	const onHandleSelectNotification = useCallback(
		async (notification) => {
			// awaited so the unread count refetch triggered by navigating sees it
			await onHandleMarkAsRead(notification.id);

			if (!hasNotificationRoute(notification.notificationType)) return;

			let route = null;

			try {
				route = await getNotificationRoute(notification.notificationType, notification.referenceId);
			} catch (error) {
				serveRequestErrors(error);
			}

			if (!route) return;

			// dismissed before navigating so the modal releases its focus trap
			onHandleDismiss();

			navigate(route);
		},
		[navigate, onHandleDismiss, onHandleMarkAsRead]
	);

	useEffect(() => {
		const container = containerRef.current;
		if (container) {
			container.addEventListener("scroll", handleScroll);
			return () => container.removeEventListener("scroll", handleScroll);
		}
	}, [handleScroll]);

	useEffect(() => {
		if (!loading && hasMore && notifications.length > 0) {
			const container = containerRef.current;
			if (container && container.scrollHeight <= container.clientHeight) {
				setTimeout(() => onHandleGetList(cursorIdRef.current), 100);
			}
		}
	}, [notifications, loading, hasMore, onHandleGetList]);

	useImperativeHandle(ref, () => ({
		onHandleShow: onHandleShow,
		onHandleDismiss: onHandleDismiss,
	}));

	return (
		<>
			<Modal classes={{ root: "app-notification-modal" }} open={visible} onClose={onHandleDismiss} aria-labelledby="notification-modal" aria-describedby="notification-modal">
				<div className="notification-modal">
					<div className="notification-modal__header">
						<div className="notification-modal__header--row">
							<span className="notification-modal__header--row--title">Notifications</span>
							<img className="notification-modal__header--row--close-icon" src={closeIcon} onClick={() => onHandleDismiss()} alt="close" />
						</div>
					</div>
					<div className="notification-modal__body" ref={containerRef}>
						{notifications.length > 0 && (
							<>
								<div className="notification-modal__mark-all-as-read">
									<span className="notification-modal__mark-all-as-read--text" onClick={() => onHandleMarkAllAsRead()}>
										Mark all as read
									</span>
								</div>
								{Object.entries(groupedNotifications).map(([label, notification]) => (
									<div key={label} className="notification-modal__notification-group">
										<h1 className="notification-modal__notification-header">{label}</h1>
										{notification.map((n) => {
											const unreadDotClassNames = classNames({
												"notification-modal__unread-dot": true,
												hidden: n.isRead,
											});

											const rowClassNames = classNames({
												"notification-modal__row": true,
												"notification-modal__row--static": !hasNotificationRoute(n.notificationType),
											});

											return (
												<div key={n.id} className={rowClassNames} onClick={() => onHandleSelectNotification(n)}>
													<span className={unreadDotClassNames}></span>
													<div className="notification-modal__row--profile" style={{ backgroundImage: `url(${n.userAvatar})` }} />
													<p className="notification-modal__row--text">{n.content}</p>
													<span className="notification-modal__row--timestamp">{n.timestamp}</span>
												</div>
											);
										})}
									</div>
								))}
								{loading && (
									<div className="notification-modal__loading">
										<span className="notification-modal__loading--spinner" />
										<span className="notification-modal__loading--text">Loading…</span>
									</div>
								)}
							</>
						)}
						{(!hasMore || (!loading && notifications.length === 0)) && <p className="notification-modal__end">- The End -</p>}
					</div>
				</div>
			</Modal>
		</>
	);
};

export default memo(forwardRef(AppNotificationModal));
